// LT-349 (LT-325 C2): Campaigns list page over the LT-348 /campaigns API.
// Wraps <PageShell>, uses the DS v1 primitives, includes status + owner
// filters and search-by-name. Archived rows hidden by default; toggle
// surfaces them via include_archived=true on the API.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Search, Workflow } from 'lucide-react';

import PageShell from '../../components/PageShell';
import {
  Button,
  DataTable,
  EmptyState,
  Input,
  Pill,
  Select,
  type ColumnDef,
  type PillIntent,
} from '../../ui';
import { campaignsAPI, extractCampaignApiError } from '../../services/campaignsAPI';
import type { Campaign, CampaignStatus } from '../../types/campaign';
import CampaignOnboardingBanner from './CampaignOnboardingBanner';

const STATUS_OPTIONS: Array<{ value: '' | CampaignStatus; label: string }> = [
  { value: '', label: 'All statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'enrolment_closed', label: 'Enrolment closed' },
  { value: 'completed', label: 'Completed' },
  { value: 'force_ended', label: 'Force-ended' },
];

const STATUS_INTENT: Record<CampaignStatus, PillIntent> = {
  draft: 'accent',
  active: 'ok',
  paused: 'warn',
  enrolment_closed: 'info',
  completed: 'neutral',
  force_ended: 'danger',
  archived: 'neutral',
};

// LT-549: which-path-created-this badge. 'wizard' | 'upload' (Campaign.creation_source).
type CreationSource = Campaign['creation_source'];

const PATH_OPTIONS: Array<{ value: '' | CreationSource; label: string }> = [
  { value: '', label: 'All paths' },
  { value: 'wizard', label: 'Wizard' },
  { value: 'upload', label: 'Upload' },
];

const PATH_META: Record<CreationSource, { label: string; intent: PillIntent; tip: string }> = {
  wizard: {
    label: 'Wizard',
    intent: 'info',
    tip: 'Created via the step-by-step wizard — edits use the same path.',
  },
  upload: {
    label: 'Upload',
    intent: 'accent',
    tip: 'Created via the upload-a-brief flow — edits use the same path.',
  },
};

export function CampaignPathPill({ source }: { source: CreationSource }) {
  const meta = PATH_META[source] ?? PATH_META.wizard;
  return (
    <span title={meta.tip}>
      <Pill intent={meta.intent}>{meta.label}</Pill>
    </span>
  );
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}

export function CampaignStatusPill({ status }: { status: CampaignStatus }) {
  return <Pill intent={STATUS_INTENT[status]}>{status.replace(/_/g, ' ')}</Pill>;
}

const CampaignsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [statusFilter, setStatusFilter] = useState<'' | CampaignStatus>('');
  const [pathFilter, setPathFilter] = useState<'' | CreationSource>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCampaigns = useCallback(
    (signal?: { cancelled: boolean }) => {
      setLoading(true);
      setError(null);
      campaignsAPI
        .list({
          status: statusFilter ? [statusFilter] : undefined,
          include_archived: showArchived,
          limit: 200,
        })
        .then((rows) => {
          if (signal?.cancelled) return;
          setCampaigns(rows);
        })
        .catch((err) => {
          if (signal?.cancelled) return;
          setError(extractCampaignApiError(err, 'Failed to load campaigns.'));
        })
        .finally(() => {
          if (signal?.cancelled) return;
          setLoading(false);
        });
    },
    [statusFilter, showArchived],
  );

  useEffect(() => {
    const signal = { cancelled: false };
    fetchCampaigns(signal);
    return () => {
      signal.cancelled = true;
    };
  }, [fetchCampaigns]);

  const filtered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (pathFilter && c.creation_source !== pathFilter) return false;
      if (q && !c.name.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [campaigns, searchTerm, pathFilter]);

  const columns: ColumnDef<Campaign>[] = useMemo(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        sortValue: (c) => c.name.toLowerCase(),
        accessor: (c) => (
          <div>
            <Link
              to={`/campaigns/${c.id}`}
              className="text-accent hover:text-accent-hover hover:underline"
            >
              {c.name}
            </Link>
            {c.description ? (
              <div className="text-xs text-slate-400 mt-0.5">{c.description}</div>
            ) : null}
          </div>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        accessor: (c) => <CampaignStatusPill status={c.status} />,
      },
      {
        id: 'creation_source',
        header: 'Path',
        accessor: (c) => <CampaignPathPill source={c.creation_source} />,
      },
      {
        id: 'launched_at',
        header: 'Launched',
        sortable: true,
        sortValue: (c) =>
          c.launched_at ? new Date(c.launched_at).getTime() : 0,
        accessor: (c) => (
          <span className="text-slate-400">{formatDate(c.launched_at)}</span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        sortable: true,
        sortValue: (c) => new Date(c.created_at).getTime(),
        accessor: (c) => (
          <span className="text-slate-400">{formatDate(c.created_at)}</span>
        ),
      },
    ],
    [],
  );

  return (
    <PageShell>
      <CampaignOnboardingBanner />
      <header className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-wider text-slate-500 mb-1">
            Campaigns
          </div>
          <h1 className="text-2xl font-semibold text-slate-50">Campaigns</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-xl">
            Operator-facing initiatives. Each Campaign carries an audience,
            owner, status lifecycle, goals + KPIs, and a retry strategy.
          </p>
        </div>
        <Button intent="primary" onClick={() => navigate('/campaigns/create')}>
          <Plus size={16} strokeWidth={2.5} />
          New Campaign
        </Button>
      </header>

      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 pointer-events-none"
          />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name…"
            className="pl-9"
            aria-label="Search campaigns"
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as '' | CampaignStatus)}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value || '_all'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <Select
          value={pathFilter}
          onChange={(e) => setPathFilter(e.target.value as '' | CreationSource)}
          aria-label="Filter by creation path"
        >
          {PATH_OPTIONS.map((opt) => (
            <option key={opt.value || '_all_paths'} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </Select>
        <label className="inline-flex items-center gap-2 text-xs text-slate-400 ml-2">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            className="accent-accent"
          />
          Show archived
        </label>
        <span className="ml-auto text-xs text-slate-500">
          {filtered.length} {filtered.length === 1 ? 'campaign' : 'campaigns'}
        </span>
      </div>

      {error ? (
        <div className="mb-4 px-3 py-2 rounded-ds-lg border border-danger-border bg-danger-tint text-danger-text text-sm">
          {error}
        </div>
      ) : null}

      {loading ? (
        <div className="text-sm text-slate-500 px-3 py-6">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          surface="campaigns_list"
          icon={Workflow}
          headline={searchTerm ? 'No campaigns match your search' : 'No campaigns yet'}
          subhead={
            searchTerm
              ? 'Try a different search term or clear the filters.'
              : 'Create your first Campaign to wrap a Sequence with an audience, owner, and lifecycle.'
          }
          primary={{
            label: searchTerm ? 'Clear filters' : 'Create your first Campaign',
            onClick: searchTerm
              ? () => {
                  setSearchTerm('');
                  setStatusFilter('');
                  setPathFilter('');
                }
              : () => navigate('/campaigns/create'),
          }}
        />
      ) : (
        <DataTable<Campaign>
          data={filtered}
          columns={columns}
          getRowKey={(c) => c.id}
          onRowClick={(c) => navigate(`/campaigns/${c.id}`)}
          ariaLabel="Campaigns"
        />
      )}
    </PageShell>
  );
};

export default CampaignsListPage;
